import React from 'react';
import ReactDOM from 'react-dom';
import { Produto } from '../types/produtos';

interface PrimaryAction {
  label: string;
  className?: string;
  onClick: (produto: Produto) => void;
}

interface ProdutosRelacionadosModalProps {
  searchTerm: string;
  produtos: Produto[];
  primaryAction: PrimaryAction;
  onContinue: () => void;
  onClose: () => void;
}

const formatEstoque = (estoque: number) =>
  new Intl.NumberFormat('pt-BR').format(estoque);

const EstoqueIndicator: React.FC<{ estoque: number }> = ({ estoque }) => {
  if (estoque === 0)
    return <span className="badge bg-danger">Zerado</span>;
  if (estoque <= 10)
    return <span className="badge bg-warning text-dark">{formatEstoque(estoque)}</span>;
  return <span className="badge bg-success">{formatEstoque(estoque)}</span>;
};

const ProdutosRelacionadosModal: React.FC<ProdutosRelacionadosModalProps> = ({
  searchTerm,
  produtos,
  primaryAction,
  onContinue,
  onClose,
}) => {
  const modal = (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      role="dialog"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="ti ti-alert-triangle text-warning me-2"></i>
              Produtos Relacionados Encontrados
            </h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Fechar" />
          </div>

          <div className="modal-body">
            <div className="alert alert-warning d-flex align-items-start mb-3">
              <i className="ti ti-info-circle me-2 mt-1 flex-shrink-0"></i>
              <span>
                Encontramos <strong>{produtos.length}</strong>{' '}
                {produtos.length === 1 ? 'produto relacionado' : 'produtos relacionados'} à
                pesquisa <strong>"{searchTerm}"</strong>. Verifique antes de criar um novo.
              </span>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Nome</th>
                    <th>Unidade</th>
                    <th>Estoque</th>
                    <th className="text-end">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {produtos.map((produto) => (
                    <tr key={produto.id}>
                      <td>
                        <div className="fw-medium">{produto.nome}</div>
                        {produto.marca && (
                          <small className="text-muted">{produto.marca}</small>
                        )}
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border">
                          {produto.unidade_medida || '—'}
                        </span>
                      </td>
                      <td>
                        <EstoqueIndicator estoque={produto.estoque} />
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className={`btn btn-sm ${primaryAction.className || 'btn-outline-primary'}`}
                          onClick={() => primaryAction.onClick(produto)}
                        >
                          {primaryAction.label}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="modal-footer justify-content-between">
            <button type="button" className="btn btn-light" onClick={onClose}>
              <i className="ti ti-x me-1"></i>
              Fechar
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={onContinue}>
              <i className="ti ti-arrow-right me-1"></i>
              Continuar mesmo assim
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default ProdutosRelacionadosModal;
